import type { LoginCredentials, LoginResponse, RefreshTokenResponse } from "@/schemas/login.schema";
import type { SignupCredentials } from "@/schemas/signup.schema";
import type { UserProfile } from "@/schemas/user-profile.schema";
import { api } from "@/services/api";

export interface MfaRequired {
  mfa_required: boolean;
  email: string;
  full_name: string;
  language: "pt" | "en";
}

export interface VerifyMfaBody {
  type: "auth" | "recovery" | "email";
  code: string;
}

export interface OktaBody {
  access_token: string;
  domain: string;
}

export default {
  async sendCode(email: string, turnstileToken?: string): Promise<void> {
    await api.post("/send-code", {
      email,
      ...(turnstileToken === undefined ? {} : { turnstile_token: turnstileToken }),
    });
  },

  async confirmCode(code: string, email: string): Promise<{ email: string }> {
    const { data } = await api.get<{ email: string }>(`/confirm-code/${code}`, {
      params: { email },
    });

    return data;
  },

  async register(credentials: SignupCredentials, access_code: string): Promise<LoginResponse> {
    const { data } = await api.post<LoginResponse>("/register", credentials, {
      headers: {
        "x-sign-up-code": access_code,
      },
    });

    return data;
  },

  async login(
    credentials: LoginCredentials & { mfa?: VerifyMfaBody }
  ): Promise<LoginResponse | MfaRequired> {
    const { data } = await api.post<LoginResponse>("/login", credentials);
    return data;
  },

  async googleLogin(
    credentials: { token: string } & { mfa?: VerifyMfaBody }
  ): Promise<LoginResponse | MfaRequired> {
    const { data } = await api.post<LoginResponse>("/oauth/google/login", credentials);

    return data;
  },

  oktaLogin: async (body: OktaBody) => {
    const { data } = await api.post<LoginResponse>("/oauth/okta/login", body);

    return data;
  },

  async microsoftLogin(
    credentials: { token: string } & { mfa?: VerifyMfaBody }
  ): Promise<LoginResponse | MfaRequired> {
    const { data } = await api.post<LoginResponse>("/oauth/microsoft/login", credentials);

    return data;
  },

  async me(): Promise<UserProfile> {
    const { data } = await api.get<UserProfile>("/me");
    return data;
  },
  async refresh(options?: { refreshUser?: boolean }): Promise<string> {
    const { data } = await api.get<RefreshTokenResponse>("/refresh-session", {
      params: {
        refresh_user: options?.refreshUser,
      },
    });

    return data.access_token;
  },
  async logout(): Promise<void> {
    await api.post("/logout");
  },

  async googleSignup(token: string): Promise<{ code: string; email: string }> {
    const { data } = await api.post("/oauth/google/signup", {
      token,
    });

    return data;
  },

  async microsoftSignup(token: string): Promise<{ code: string; email: string }> {
    const { data } = await api.post("/oauth/microsoft/signup", {
      token,
    });

    return data;
  },

  async forgotPassword(email: string): Promise<void> {
    await api.post("/forgot-password", { email });
  },

  async switchOrganization(organizationId: string): Promise<LoginResponse> {
    const { data } = await api.post<LoginResponse>("/switch-organization", {
      organization_id: organizationId,
    });
    return data;
  },

  async createOrganization(body: { name: string; currency?: string }): Promise<{ id: string; name: string; key: string }> {
    const { data } = await api.post<{ id: string; name: string; key: string }>("/organizations", body);
    return data;
  },

  async getInvitation(token: string): Promise<{ organization_name: string; organization_id: string; user_name: string; email: string; role: string; is_new_user: boolean }> {
    const { data } = await api.get<{ organization_name: string; organization_id: string; user_name: string; email: string; role: string; is_new_user: boolean }>(
      `/invitations/${token}`,
    );
    return data;
  },

  async acceptInvitation(token: string): Promise<{ organization_id: string; organization_name: string; role: string }> {
    const { data } = await api.post<{ organization_id: string; organization_name: string; role: string }>(
      `/invitations/${token}/accept`,
    );
    return data;
  },

  async declineInvitation(token: string): Promise<void> {
    await api.post(`/invitations/${token}/decline`);
  },

  async setupAndAcceptInvitation(token: string, body: { full_name: string; password: string }): Promise<{ access_token: string; organization_id: string; organization_name: string; role: string }> {
    const { data } = await api.post<{ access_token: string; organization_id: string; organization_name: string; role: string }>(
      `/invitations/${token}/setup-and-accept`,
      body,
    );
    return data;
  },

  async leaveOrganization(orgId: string): Promise<{ message: string; next_organization_id: string | null }> {
    const { data } = await api.post<{ message: string; next_organization_id: string | null }>(`/organizations/${orgId}/leave`);
    return data;
  },

  async transferOwnership(orgId: string, targetUserId: string): Promise<{ message: string }> {
    const { data } = await api.post<{ message: string }>(`/organizations/${orgId}/transfer-ownership`, { target_user_id: targetUserId });
    return data;
  },

  async deleteOrganization(orgId: string): Promise<{ message: string }> {
    const { data } = await api.post<{ message: string }>(`/organizations/${orgId}/delete`);
    return data;
  },
};
